import * as React from 'react'

import apis from '@app/api/api'

import Theme from '@theme/Theme'
import styled from '@theme/styled-components'
import useI18n from '@store/i18n/useI18n'

import lettersName from '@utils/LettersName'

import Searchbar from '@components/searchbar/searchbar'
import Images from '@components/images/images'
import Button from '@components/button/button'

import Loader from 'react-loader-spinner'
import { ImWarning } from 'react-icons/im'
import { FiChevronRight, FiCheck } from 'react-icons/fi'
import { RiInformationLine } from 'react-icons/ri'
import { motion, container, item } from '@utils/Motion'
import { compareAsc } from 'date-fns'
import Swal from 'sweetalert2'
import DownloadingComponent from '@utils/ImageDownloader'
import isAfter from 'date-fns/fp/isAfter'

interface Props {
  actualTab: Tab
  steps: BusinessStep[]
  navigation: Navigation
  screenStatus: ScreenStatus
  business?: AllBusinessDetails
  declarations: BusinessDeclaration[]
  sectionPictures: BusinessDetailsPicture[]
  setSectionPictures: (pictures: BusinessDetailsPicture[]) => void
  commentBusiness: (c: BusinessDetailsComment) => void
}

const DetailBusiness = ({
  steps,
  business,
  declarations,
  actualTab,
  navigation,
  screenStatus,
  commentBusiness,
  sectionPictures,
  setSectionPictures,
}: Props) => {
  const i18n = useI18n()

  const [selectedStep, setSelectedStep] = React.useState<BusinessStep | undefined>(undefined)
  const [selectedTheme, setSelectedTheme] = React.useState<BusinessTheme | undefined>(undefined)
  const [text, setText] = React.useState<string>('')
  const [downloadPictures, setDownloadPictures] = React.useState<boolean>(false)
  const [downloading, setDownloading] = React.useState<boolean>(false)
  const [picturesToDownload, setPicturesToDownload] = React.useState<PicturesToDownload[]>([])
  const [commentLoading, setCommentLoading] = React.useState<boolean>(false)

  React.useEffect(() => {
    setSelectedStep(undefined)
    setSelectedTheme(undefined)
    setText('')
  }, [actualTab])

  const findStepThemeLibelle = (stepId: string, themeId: string) => {
    const step = steps.find((s) => s.id === stepId)
    const theme = step && step.themes.find((t) => t.id === themeId)

    return {
      stepLibelle: step ? step.wording : 'NO_STEP',
      themeLibelle: theme ? theme.wording : 'NO_THEME',
    }
  }

  const onPressPicture = (picture: BusinessDetailsPicture, businessName: string) => {
    if (picturesToDownload.findIndex((p) => p.id === picture.id) > -1) {
      setPicturesToDownload(picturesToDownload.filter((pic) => pic.id !== picture.id))
    } else {
      const stepTheme = findStepThemeLibelle(picture.idStep, picture.idTheme)

      setPicturesToDownload([
        ...picturesToDownload,
        {
          id: picture.id,
          path: picture.picture,
          name: `${businessName}_${stepTheme.stepLibelle}_${stepTheme.themeLibelle}`,
        },
      ])
    }
  }

  const renderSteps = (b: AllBusinessDetails) => {
    return (
      <>
        <CategoryContainer
          as={motion.div}
          className="container"
          variants={container}
          initial="hidden"
          animate="visible">
          <Title>{i18n.t('components.businessDetails.step')}</Title>
          {steps
            .sort((a, b) => a.index - b.index)
            .map((step) => {
              const countPhotos = b.pictures.filter((picture) => picture.idStep === step.id).length
              const selectedCheck = !!selectedStep && selectedStep.id === step.id
              const stepBreakpoints: BusinessDeclaration[] = declarations.filter(
                (d) => !!d.breakpoint && d.description.idStep === step.id
              )
              const stepHasUnresolveBreakpoints = !!stepBreakpoints.find((d) => !!d.breakpoint && !d.breakpoint.release)

              return (
                <ItemContainer
                  clickable={countPhotos > 0}
                  selected={selectedCheck}
                  key={step.id}
                  onClick={() => {
                    if (countPhotos > 0) {
                      setSelectedStep(step)
                      setSelectedTheme(undefined)
                    }
                  }}>
                  <CheckContainer>
                    {countPhotos > 0 && <RiInformationLine size={20} color={Theme.colors.primary_blue} />}
                  </CheckContainer>
                  <Item>{step.wording}</Item>
                  {stepBreakpoints.length !== 0 && (
                    <BreakpointContainer>
                      {stepHasUnresolveBreakpoints ? (
                        <ImWarning size={20} color={Theme.colors.secondary_yellow} />
                      ) : (
                        <FiCheck size={20} color={Theme.colors.primary_green} />
                      )}
                    </BreakpointContainer>
                  )}
                  {selectedCheck && <FiChevronRight size={20} />}
                </ItemContainer>
              )
            })}
        </CategoryContainer>
        {!!selectedStep && renderThemes(b, selectedStep)}
      </>
    )
  }

  const renderThemes = (b: AllBusinessDetails, s: BusinessStep) => {
    return (
      <>
        <CategoryContainer
          greyBackground
          as={motion.div}
          className="container"
          variants={container}
          initial="hidden"
          animate="visible">
          <Title>{i18n.t('components.businessDetails.theme')}</Title>
          {s.themes
            .sort((a, b) => a.index - b.index)
            .map((t) => {
              const countPhotos = b.pictures.filter((picture) => picture.idTheme === t.id).length
              const selectedCheck = !!selectedTheme && selectedTheme.id === t.id
              const themeBreakpoints: Declaration[] = declarations.filter(
                (d) => !!d.breakpoint && d.description.idStep === s.id && d.description.idTheme === t.id
              )
              const themeHasUnresolveBreakpoints = !!themeBreakpoints.find(
                (d) => !!d.breakpoint && !d.breakpoint.release
              )

              return (
                <ItemContainer
                  clickable={countPhotos > 0}
                  selected={selectedCheck}
                  key={t.id}
                  onClick={() => countPhotos > 0 && setSelectedTheme(t)}>
                  <CheckContainer>
                    {countPhotos > 0 && <RiInformationLine size={20} color={Theme.colors.primary_blue} />}
                  </CheckContainer>
                  <Item>{t.wording}</Item>
                  {themeBreakpoints.length !== 0 && (
                    <BreakpointContainer>
                      {themeHasUnresolveBreakpoints ? (
                        <ImWarning size={20} color={Theme.colors.secondary_yellow} />
                      ) : (
                        <FiCheck size={20} color={Theme.colors.primary_green} />
                      )}
                    </BreakpointContainer>
                  )}
                  {selectedCheck && <FiChevronRight size={16} />}
                </ItemContainer>
              )
            })}
        </CategoryContainer>
        {!!selectedTheme && renderDeclarations(b, s, selectedTheme)}
      </>
    )
  }

  const renderDeclarations = (b: AllBusinessDetails, s: BusinessStep, t: BusinessTheme) => {
    const filteredPictures = b.pictures.filter((picture) => picture.idStep === s.id && picture.idTheme === t.id)
    const groupPictures = filteredPictures.reduce((acc, cur) => {
      const currentGroup = acc.find((p) => p.idDeclaration === cur.idDeclaration)
      const currentHasBreakpoint = declarations.find((d) => d.id === cur.idDeclaration && !!d.breakpoint)
      const currentHasUnresolvedBreakpoint =
        !!currentHasBreakpoint && !!currentHasBreakpoint.breakpoint && !currentHasBreakpoint.breakpoint.release

      return !!currentGroup
        ? [
            ...acc.filter((p) => p.idDeclaration !== cur.idDeclaration),
            {
              ...currentGroup,
              countPictures: currentGroup.countPictures + 1,
            },
          ]
        : [
            ...acc,
            {
              idDeclaration: cur.idDeclaration,
              dateDeclaration: cur.dateDeclaration,
              countPictures: 1,
              hasUnresolveBreakpoint: currentHasBreakpoint && currentHasUnresolvedBreakpoint,
            },
          ]
    }, [] as PictureGroups[])
    const filteredGroupPictures: PictureGroups[] = groupPictures.sort((a, b) =>
      isAfter(new Date(a.dateDeclaration), new Date(b.dateDeclaration)) ? 1 : -1
    )

    return (
      <CategoryContainer as={motion.div} className="container" variants={container} initial="hidden" animate="visible">
        <Title>{i18n.t('components.businessDetails.declaration')}</Title>
        {filteredGroupPictures.map((pictures) => (
          <ItemContainer
            key={pictures.idDeclaration}
            clickable
            selected={false}
            onClick={() => navigation.push(`/business/${b.id}/declaration/${pictures.idDeclaration}`)}>
            <Item underline>
              {i18n.t('components.businessDetails.numberPhotos', {
                count: pictures.countPictures,
                date: new Date(pictures.dateDeclaration),
              })}
            </Item>
            {pictures.hasUnresolveBreakpoint !== undefined && (
              <BreakpointContainer>
                {pictures.hasUnresolveBreakpoint ? (
                  <ImWarning size={20} color={Theme.colors.secondary_yellow} />
                ) : (
                  <FiCheck size={20} color={Theme.colors.primary_green} />
                )}
              </BreakpointContainer>
            )}
          </ItemContainer>
        ))}
      </CategoryContainer>
    )
  }

  const deletePicture = (imageId: string, declaration: Declaration) => {
    if (declaration) {
      const newDeclaration: Declaration = {
        ...declaration,
        pictures: declaration?.pictures.filter((oldPicture) => oldPicture.id !== imageId) ?? [],
      }

      if (newDeclaration) {
        apis.declaration
          .edit(newDeclaration)
          .then(() => {
            setSectionPictures([...sectionPictures.filter((sectionPicture) => sectionPicture.id !== imageId)])
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.success.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.success.text`),
              icon: 'success',
            })
          })
          .catch(() => {
            Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.failure.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.failure.text`),
              icon: 'error',
            })
          })
      }
    }
  }

  const confirmDeletePhoto = (imageId: string) => {
    if (business) {
      const oldDeclaration = declarations.find((d) => {
        return d.pictures.find((p) => p.id === imageId)
      })
      if (oldDeclaration) {
        return oldDeclaration?.breakpoint
          ? Swal.fire({
              title: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.title`),
              text: i18n.t(`components.detailsChantier.alertDeleting.breakpoint.text`),
              icon: 'error',
            })
          : Swal.fire({
              title: i18n.t('components.detailsChantier.alertDeleting.title'),
              text: i18n.t('components.detailsChantier.alertDeleting.text'),
              showCancelButton: true,
              confirmButtonText: i18n.t('components.adminCompanies.alertCancelling.confirm'),
              confirmButtonColor: 'green',
              cancelButtonText: i18n.t('components.adminCompanies.alertCancelling.cancel'),
              cancelButtonColor: 'red',
            }).then((result) => {
              if (result.isConfirmed) {
                Swal.fire({
                  didOpen: () => {
                    Swal.showLoading()
                  },
                })
                deletePicture(imageId, oldDeclaration)
              }
            })
      }
    }
  }

  const renderAllPictures = (b: AllBusinessDetails) => {
    return (
      <AllImagesContainer>
        <ButtonsContainer>
          <ActionContainer hide={false}>
            <Button
              title={i18n.t(
                `components.businessDetails.buttons.${
                  downloadPictures
                    ? picturesToDownload.length !== 0
                      ? 'downloadCountPictures'
                      : 'selectPictures'
                    : 'downloadPictures'
                }`,
                {
                  count: picturesToDownload.length,
                }
              )}
              onClick={() => {
                if (!downloadPictures) {
                  setDownloadPictures(true)
                } else {
                  setDownloading(true)
                }
              }}
              type={downloadPictures ? 'navigation' : 'classic'}
              status={
                downloadPictures && picturesToDownload.length === 0 ? 'disabled' : downloading ? 'loading' : 'active'
              }
              size="medium"
              icon="download"
            />
          </ActionContainer>
          {downloadPictures && (
            <Button
              title={i18n.t(`components.businessDetails.buttons.cancel`)}
              onClick={() => {
                setDownloadPictures(false)
                setPicturesToDownload([])
              }}
              type="classic"
              status="active"
              size="medium"
              icon="cancel"
            />
          )}
        </ButtonsContainer>
        <ImagesContainer as={motion.div} className="item" variants={item} initial="hidden" animate="visible">
          {sectionPictures.length === 0 ? (
            <NoPhotoContainer>
              <NoPhotoLibelle>{i18n.t('components.detailsChantier.noPhoto')}</NoPhotoLibelle>
            </NoPhotoContainer>
          ) : (
            sectionPictures.map((picture, index) => (
              <Images
                key={picture.id}
                imageId={picture.id}
                index={index}
                images={sectionPictures}
                size={140}
                fromdetailbusiness
                removingPictures={downloadPictures}
                setSelectedPicture={() => onPressPicture(picture, b.title)}
                selected={picturesToDownload.findIndex((p) => p.id === picture.id) > -1}
                status={picture.status}
                deletePicture={downloadPictures ? undefined : confirmDeletePhoto}
              />
            ))
          )}
        </ImagesContainer>
        {downloading && (
          <DownloadingComponent
            pictures={picturesToDownload}
            cancel={() => {
              setDownloading(false)
              setPicturesToDownload([])
              setDownloadPictures(false)
            }}
          />
        )}
      </AllImagesContainer>
    )
  }

  const renderComments = (b: AllBusinessDetails) => {
    return (
      <>
        <CommentsContainer
          as={motion.div}
          className="container"
          variants={container}
          initial="hidden"
          animate="visible">
          {b.comments.length !== 0 ? (
            b.comments
              .sort((a, b) => compareAsc(new Date(a.date), new Date(b.date)))
              .map((comment, i) => {
                return (
                  <CommentItem as={motion.div} key={comment.id} className="item" variants={item}>
                    <InfosContainer>
                      <NameContainer>
                        <Name>{lettersName(comment.firstname, comment.lastname)}</Name>
                      </NameContainer>
                      <FullName>
                        {i18n.t('components.businessDetails.fullName', {
                          firstname: comment.firstname,
                          lastname: comment.lastname,
                          role: i18n.t(`userRoles.${comment.role}`),
                        })}
                      </FullName>
                      <DateComment>
                        {i18n.t('components.businessDetails.dateComment', {
                          date: new Date(comment.date),
                        })}
                      </DateComment>
                    </InfosContainer>
                    <Comment>{comment.comment}</Comment>
                  </CommentItem>
                )
              })
          ) : (
            <NoComment>{i18n.t('components.businessDetails.noComment')}</NoComment>
          )}
        </CommentsContainer>
        <InputContainer>
          <Searchbar
            placeholder={i18n.t('components.businessDetails.placeholderComment')}
            text={text}
            setText={setText}
            onSend={() => sendComment(b.id)}
            loading={commentLoading}
          />
        </InputContainer>
      </>
    )
  }

  const sendComment = (businessId: string) => {
    if (!!text) {
      setCommentLoading(true)
      apis.business
        .comment(businessId, text)
        .then((c) => {
          setCommentLoading(false)
          commentBusiness(c)
          setText('')
        })
        .catch((err) => {
          Swal.fire({
            title: i18n.t('components.businessDetails.commentFailure.title'),
            text: i18n.t('components.businessDetails.commentFailure.text'),
            icon: 'question',
            showCancelButton: false,
            confirmButtonColor: '#3A8641',
            confirmButtonText: i18n.t('components.businessDetails.commentFailure.confirm'),
          })
          setCommentLoading(false)
        })
    }
  }

  return (
    <Container loading={screenStatus === 'loading'}>
      {screenStatus === 'loading' ? (
        <StatusContainer>
          <Loader type="ThreeDots" color={Theme.colors.classicButtonLoader} height={30} />
        </StatusContainer>
      ) : screenStatus === 'error' || !business ? (
        <ErrorContainer>
          <ErrorLibelle>{i18n.t('components.detailsChantier.error')}</ErrorLibelle>
        </ErrorContainer>
      ) : (
        <StepThemeContainer displayPictures={actualTab === 'pictures'}>
          {actualTab === 'state'
            ? renderSteps(business)
            : actualTab === 'comments'
            ? renderComments(business)
            : renderAllPictures(business)}
        </StepThemeContainer>
      )}
    </Container>
  )
}

export default DetailBusiness

const Container = styled('div')<{ loading: boolean }>`
  flex: 1;
  overflow: hidden;
  overflow-y: scroll;
  ${(props) => (props.loading ? 'padding-bottom: 0px; padding-top: 53px;' : 'padding-bottom: 60px; padding-top: 10px;')}
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`

const StatusContainer = styled('div')`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  height: 100%;
`
const ErrorContainer = styled('div')`
  height: 100%;
  display: flex;
  margin-top: 56px;
  align-items: center;
  justify-content: center;
`
const ErrorLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
  margin: 40px;
`
const StepThemeContainer = styled('div')<{ displayPictures: boolean }>`
  margin: ${(props) => (props.displayPictures ? '70px 50px 0px' : '150px 50px 0px')};
  flex-direction: row;
  display: flex;
  flex: 1;
  flex-grow: 0;
`
const CategoryContainer = styled('div')<{ greyBackground?: boolean }>`
  overflow-y: scroll;
  background-color: ${(props) => (!!props.greyBackground ? props.theme.colors.greyBackground : '#ffffff')};
  padding: 16px 0px 0px;
  flex: 1;
`
const Title = styled('div')`
  ${(props) => props.theme.fonts.title};
  padding: 0px 16px 20px 16px;
`
const ItemContainer = styled('div')<{ clickable: boolean; selected: boolean }>`
  display: flex;
  min-height: 36px;
  padding: 0px 12px;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start
    ${(props) =>
      props.clickable &&
      `
  cursor: pointer;
  & > div {
    cursor: pointer;
  }
  `}
    ${(props) =>
      props.clickable &&
      `
  cursor: pointer;
  `}
    ${(props) => props.selected && `background-color: ${props.theme.colors.backgroundSelected};`};
  margin: 1px;
`
const Item = styled('div')<{ underline?: boolean }>`
-  ${(props) => props.theme.fonts.noItem};
  color: ${(props) => props.theme.colors.titleColor};
  padding-right: 10px;
  padding-left: 10px;
  ${(props) => !!props.underline && 'text-decoration-line: underline;'}
`
const CheckContainer = styled('div')`
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
`
const CommentsContainer = styled('div')`
  overflow-y: scroll;
  padding: 16px 0px 80px;
  flex: 1;
`
const CommentItem = styled('div')`
  border-radius: 10px;
  margin-bottom: 20px;
  padding: 20px;
  background-color: #ffffff;
`
const Comment = styled('div')`
  ${(props) => props.theme.fonts.label};
  color: ${(props) => props.theme.colors.titleColor};
`
const NameContainer = styled('div')`
  background-color: ${(props) => props.theme.colors.primary_blue};
  height: 60px;
  width: 60px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`
const Name = styled('div')`
  ${(props) => props.theme.fonts.calendarLibelle};
  color: ${(props) => props.theme.colors.secondary_text};
  text-align: center;
`
const InfosContainer = styled('div')`
  flex-direction: row;
  display: flex;
  margin-bottom: 16px;
  align-items: center;
`
const FullName = styled('div')`
  ${(props) => props.theme.fonts.boldLabel};
  color: ${(props) => props.theme.colors.markerEnded};
  flex: 1;
  margin-right: 12px;
`
const DateComment = styled('div')`
  ${(props) => props.theme.fonts.selectLibelle};
  color: ${(props) => props.theme.colors.secondary_grey};
`
const InputContainer = styled('div')`
  border: 0px solid ${(props) => props.theme.colors.backgroundSelected};
  border-top-width: 1px;
  bottom: 0px;
  padding-bottom: 40px;
  left: 500px;
  right: 50px;
  position: fixed;
  flex-direction: column;
  justify-content: flex-end;
  background-color: ${(props) => props.theme.colors.filterBackgroundColor};
`
const ImagesContainer = styled('div')`
  overflow: scroll;
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  padding: 25px;
  flex-direction: row;
`
const NoPhotoContainer = styled('div')`
  flex: 1;
`
const NoPhotoLibelle = styled('div')`
  ${(props) => props.theme.fonts.noItem};
  text-align: center;
`
const ButtonsContainer = styled('div')`
  flex-direction: row;
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 28px;
`
const AllImagesContainer = styled('div')`
  flex-direction: column;
`
const ActionContainer = styled('div')<{ hide?: boolean }>`
  ${(props) => props.hide && 'display: none;'}
  margin-right: 30px;
  margin-bottom: 12px;
`
const NoComment = styled('div')`
  text-align: center;
  ${(props) => props.theme.fonts.noItem}
`
const BreakpointContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
`
